import type {
  BaseConnection,
  SessionConfig,
  ConnectionType,
} from "./BaseConnection.js";
import { WebSocketConnection } from "./WebSocketConnection.js";
import { WebRTCConnection } from "./WebRTCConnection.js";

function determineConnectionType(config: SessionConfig): ConnectionType {
  const hasSignedUrl = "signedUrl" in config && config.signedUrl;
  const hasOrchestratorConfig = "orchestrator" in config && config.orchestrator;

  // Reject invalid combination: signedUrl only supports websocket
  // Cast needed because TS narrows signedUrl configs to connectionType?: "websocket",
  // but this guard protects against invalid configs from plain JS callers.
  if (hasSignedUrl && (config.connectionType as ConnectionType) === "webrtc") {
    throw new Error(
      "signedUrl only supports websocket connections. Remove connectionType or set it to 'websocket'."
    );
  }

  if (
    hasOrchestratorConfig &&
    (config.connectionType as ConnectionType) === "webrtc"
  ) {
    throw new Error(
      "Orchestrator sessions only support websocket connections. Remove connectionType or set it to 'websocket'."
    );
  }

  if (
    hasOrchestratorConfig &&
    (hasSignedUrl ||
      ("conversationToken" in config && config.conversationToken) ||
      config.authorization)
  ) {
    throw new Error(
      "The orchestrator option cannot be combined with signedUrl, conversationToken or authorization."
    );
  }

  // If connectionType is explicitly specified, use it
  if (config.connectionType) {
    return config.connectionType;
  }

  // If conversationToken is provided, use WebRTC
  if ("conversationToken" in config && config.conversationToken) {
    return "webrtc";
  }

  // If signedUrl is provided, use WebSocket (it only supports websocket)
  if (hasSignedUrl) {
    return "websocket";
  }

  if (hasOrchestratorConfig) {
    return "websocket";
  }

  // Infer from conversation mode: text-only uses websocket, voice uses webrtc
  return config.textOnly ? "websocket" : "webrtc";
}

export async function createConnection(
  config: SessionConfig
): Promise<BaseConnection> {
  const connectionType = determineConnectionType(config);

  switch (connectionType) {
    case "websocket":
      return WebSocketConnection.create(config);
    case "webrtc":
      return WebRTCConnection.create(config);
    default:
      throw new Error(`Unknown connection type: ${connectionType}`);
  }
}
